/**
 * F3 GPU time (PERF G1, plan `docs/plans/2026-10-15-gpu-perf-cards.md`): WebGPU timestamp queries around every render
 * and compute pass, bucketed into shadow, prepass, scene, post and other, averaged over ~30 frames and shown as one F3
 * line, so a reading separates GPU time from the `render` (CPU submit) and `idle` columns (D-969, D-970).
 *
 * - **Feature.** three r186's WebGPU backend already requests every feature the adapter offers (`timestamp-query`
 *   included) and never fails without it; this module only reads `device.features`. No feature (or WebGL2): the line
 *   reads `gpu n/a (no timestamp-query)`.
 * - **Own queries.** three's own tracking (`trackTimestamp`) is left off: its pool keys every pass by a per-frame
 *   string and allocates maps on each resolve. Instead `backend.initTimestampQuery` (called with each pass descriptor
 *   just before `beginRenderPass`/`beginComputePass`) is replaced once: while timing is active it points the
 *   descriptor's `timestampWrites` at the open slot of a small ring of query sets, else it clears them, so with F3
 *   closed (or `dev.json` `perfGpu.enabled` false) no query is written at all.
 * - **Ring.** A slot holds `framesPerSlot` frames of passes; when full it is resolved (resolveQuerySet → copy →
 *   `mapAsync`, never awaited on the main thread) and the next free slot opens. If every slot is still in flight the
 *   frame goes untimed. Results land in a ring of slot sums; the readout averages the newest slots covering
 *   `windowFrames` frames.
 * - **Classes.** `renderer.render` is wrapped (the passStats pattern): the camera of a sun shadow level → shadow; the
 *   AO prepass override material → prepass; a post quad → post; the scaled-space scene or the main camera → scene;
 *   anything else and compute passes → other. Nested renders restore the outer class on exit.
 * - **Span (FG-3, D-982).** Besides the pass sum, each frame's GPU span (first pass begin → last pass end, from the
 *   same timestamps; no extra queries) is averaged too: span − passes is the GPU idle between the frame's submits.
 * - **Allocation.** The per-frame bookkeeping allocates nothing (D-014). The WebGPU calls of a resolve (command
 *   encoder, command buffer, the `mapAsync` promise, the mapped range view) are the API's own and happen once per
 *   slot (every `framesPerSlot` frames) while F3 is open.
 * - **Chrome.** Without the `chrome://flags/#enable-webgpu-developer-features` flag Chrome quantises timestamps to
 *   100 µs; the module spots that (every timestamp a multiple of 100 µs) and the line says so.
 */
import { z } from 'zod'
import { findShadowCameras, PREPASS_MATERIAL, SCALED_SCENE } from '../render/passStats'

export const GPU_CLASSES = ['shadow', 'prepass', 'scene', 'post', 'other'] as const
export type GpuClass = (typeof GPU_CLASSES)[number]
const SHADOW = 0
const PREPASS = 1
const SCENE = 2
const POST = 3
const OTHER = 4
const CLASS_COUNT = GPU_CLASSES.length
const SHADOW_SLOTS = 4
/** Result-ring entries (slot sums kept for the rolling average). */
const RESULT_RING = 32
/** Frames between shadow-camera re-resolves while timing (a preset change rebuilds the levels). */
const RESOLVE_EVERY = 120
/** Chrome's timestamp quantisation without the developer-features flag (ns). */
const QUANTUM_NS = 100_000
/** A pass longer than this is a bad pair (counter reset, missed write) and is skipped (ms). */
const MAX_PASS_MS = 1000

// WebGPU enum values (the project has no @webgpu/types; the globals are absent under node).
const MAP_READ = 0x0001
const COPY_SRC = 0x0004
const COPY_DST = 0x0008
const QUERY_RESOLVE = 0x0200
const MAP_MODE_READ = 0x0001

/** `dev.json` `perfGpu` (Zod). */
export const gpuTimingConfigSchema = z
  .object({
    /** Time the GPU while F3 is open (false: no queries ever, no line). */
    enabled: z.boolean(),
    /** Frames the F3 average covers. */
    windowFrames: z.number().int().min(1).max(240),
    /** Frames per query set before it is resolved. */
    framesPerSlot: z.number().int().min(1).max(16),
    /** Timed passes per frame (render + compute); extra passes go untimed and the line says `(+N untimed)`. */
    maxPassesPerFrame: z.number().int().min(8).max(512),
    /** Query sets in the ring (in flight at once). */
    slots: z.number().int().min(2).max(8),
  })
  .refine((c) => c.framesPerSlot * c.maxPassesPerFrame * 2 <= 4096, { message: 'framesPerSlot × maxPassesPerFrame × 2 must be ≤ 4096 (WebGPU query set limit)' })
export type GpuTimingConfig = z.infer<typeof gpuTimingConfigSchema>

// --- the WebGPU surface used (structural; fakes in tests) ---

export interface GpuQuerySetLike {
  destroy(): void
}
export interface GpuBufferLike {
  mapAsync(mode: number, offset: number, size: number): PromiseLike<void>
  getMappedRange(offset: number, size: number): ArrayBuffer
  unmap(): void
  destroy(): void
}
export interface GpuEncoderLike {
  resolveQuerySet(querySet: GpuQuerySetLike, first: number, count: number, destination: GpuBufferLike, destinationOffset: number): void
  copyBufferToBuffer(source: GpuBufferLike, sourceOffset: number, destination: GpuBufferLike, destinationOffset: number, size: number): void
  finish(): unknown
}
export interface GpuDeviceLike {
  readonly features: { has(name: string): boolean }
  createQuerySet(descriptor: { type: 'timestamp'; count: number; label?: string }): GpuQuerySetLike
  createBuffer(descriptor: { size: number; usage: number; label?: string }): GpuBufferLike
  createCommandEncoder(): GpuEncoderLike
  readonly queue: { submit(buffers: unknown[]): void }
}
/** The pass descriptor three hands `initTimestampQuery`. */
export interface PassDescriptorLike {
  timestampWrites?: TimestampWrites | undefined
}
interface TimestampWrites {
  querySet: GpuQuerySetLike | null
  beginningOfPassWriteIndex: number
  endOfPassWriteIndex: number
}
/** What `createGpuTiming` reads and patches on three's renderer. */
export interface GpuTimingRenderer {
  render: (scene: never, camera: never) => unknown
  readonly backend: unknown
}
interface SceneWithSun {
  getObjectByName(name: string): unknown
}
interface RenderedScene {
  readonly name?: string
  readonly isQuadMesh?: boolean
  readonly overrideMaterial?: { readonly name: string } | null
}

/** Slot states. */
const FREE = 0
const RECORDING = 1
const IN_FLIGHT = 2

interface Slot {
  querySet: GpuQuerySetLike
  resolveBuffer: GpuBufferLike
  readBuffer: GpuBufferLike
  /** Class per timed pass (query pair). */
  classes: Uint8Array
  /** Query pairs written. */
  pairs: number
  /** Whole frames recorded. */
  frames: number
  /** Passes over `maxPassesPerFrame` in those frames. */
  untimed: number
  /** Pair count at the close of each recorded frame (frame f owns pairs `frameEnds[f-1] .. frameEnds[f]`). */
  frameEnds: Uint16Array
  state: number
  /** Bumped when timing stops, so a late map result of a discarded generation is dropped. */
  gen: number
  /** `gen` when the slot was submitted. */
  submitGen: number
  /** The last mapped range's view. */
  view: Uint32Array | null
  onMapped: () => void
  onFail: () => void
}

export interface GpuTimingReadout {
  /** False: no `timestamp-query` (or WebGL2). */
  available: boolean
  /** Frames averaged (0 = nothing resolved yet). */
  frames: number
  /** Sum of the classes (ms per frame). */
  totalMs: number
  /** GPU frame span: first pass begin → last pass end (ms per frame), averaged over the frames with a valid span. */
  spanMs: number
  /** Per class (ms per frame), `GPU_CLASSES` order. */
  readonly classMs: Float64Array
  /** Passes per frame that went untimed (over `maxPassesPerFrame`), averaged. */
  untimed: number
  /** Chrome quantises timestamps (no developer-features flag). */
  quantised: boolean
}

export function createGpuReadout(): GpuTimingReadout {
  return { available: false, frames: 0, totalMs: 0, spanMs: 0, classMs: new Float64Array(CLASS_COUNT), untimed: 0, quantised: false }
}

export interface GpuTiming {
  readonly available: boolean
  readonly config: GpuTimingConfig
  /** Timing on now (F3 open and `enabled`). */
  readonly active: boolean
  /** Queries issued since creation (tests: none while F3 is closed). */
  readonly queriesIssued: number
  /** Call once per frame before the frame's render: `on` = F3 visible. Closes full slots, opens the next. */
  frame(on: boolean): void
  /** Fills `out` with the rolling average (allocation-free). */
  read(out: GpuTimingReadout): GpuTimingReadout
  /** three's `initTimestampQuery` replacement (exposed for tests). */
  pass(type: string, descriptor: PassDescriptorLike): void
}

/**
 * `gpu n/a`, or `gpu 9.40 ms passes · 11.20 ms span (shadow 0.20 · prepass 0.10 · scene 5.10 · post 2.90 · other 1.20)`
 * (pure, for tests).
 */
export function formatGpuLine(r: GpuTimingReadout): string {
  if (!r.available) return 'gpu n/a (no timestamp-query)'
  if (r.frames === 0) return 'gpu … ms (measuring)'
  const c = r.classMs
  let s =
    `gpu ${r.totalMs.toFixed(2)} ms passes · ${r.spanMs.toFixed(2)} ms span (shadow ${c[SHADOW].toFixed(2)} · prepass ${c[PREPASS].toFixed(2)} · ` +
    `scene ${c[SCENE].toFixed(2)} · post ${c[POST].toFixed(2)} · other ${c[OTHER].toFixed(2)})`
  if (r.untimed > 0) s += ` (+${r.untimed.toFixed(0)} untimed)`
  if (r.quantised) s += '\ngpu ms quantised to 0.1: enable chrome://flags "WebGPU Developer Features"'
  return s
}

/** The WebGPU device when the renderer runs WebGPU with `timestamp-query`, else null. */
export function timestampDevice(renderer: Pick<GpuTimingRenderer, 'backend'>): GpuDeviceLike | null {
  const b = renderer.backend as { isWebGPUBackend?: boolean; device?: GpuDeviceLike | null; initTimestampQuery?: unknown }
  // A three without the hook we patch reads n/a instead of throwing at boot.
  if (b.isWebGPUBackend !== true || !b.device || typeof b.initTimestampQuery !== 'function') return null
  return b.device.features.has('timestamp-query') ? b.device : null
}

/**
 * Installs the timing on `renderer` (`mainCamera`: the game camera; `scene` holds the `sun`). Without the feature (or
 * with `config.enabled` false) nothing is patched and `available`/`active` stay false.
 */
export function createGpuTiming(renderer: GpuTimingRenderer, scene: SceneWithSun, mainCamera: object, config: GpuTimingConfig): GpuTiming {
  const device = config.enabled ? timestampDevice(renderer) : null
  const queriesPerSlot = config.framesPerSlot * config.maxPassesPerFrame * 2
  const bytesPerSlot = queriesPerSlot * 8
  const slots: Slot[] = []
  // Result ring: per slot sums, frames and untimed passes.
  const sums = new Float64Array(RESULT_RING * CLASS_COUNT)
  const sumFrames = new Float64Array(RESULT_RING)
  const sumUntimed = new Float64Array(RESULT_RING)
  const sumSpan = new Float64Array(RESULT_RING)
  const sumSpanFrames = new Float64Array(RESULT_RING)
  let ringHead = 0
  let ringCount = 0
  let quantised = false
  const shadowCams: (object | null)[] = [null, null, null, null]
  let cls = OTHER
  let active = false
  let open = -1
  let framePairs = 0
  let frameUntimed = 0
  let sinceResolve = RESOLVE_EVERY
  let issued = 0
  const writes: TimestampWrites = { querySet: null, beginningOfPassWriteIndex: 0, endOfPassWriteIndex: 1 }
  const submitList: unknown[] = [null]

  const resolveShadows = (): void => {
    const cams = findShadowCameras(scene)
    for (let i = 0; i < SHADOW_SLOTS; i++) shadowCams[i] = i < cams.length ? cams[i] : null
  }

  const classify = (s: RenderedScene, camera: unknown): number => {
    for (let i = 0; i < SHADOW_SLOTS; i++) if (shadowCams[i] !== null && shadowCams[i] === camera) return SHADOW
    if (s.overrideMaterial?.name === PREPASS_MATERIAL) return PREPASS
    if (s.isQuadMesh === true) return POST
    if (s.name === SCALED_SCENE || camera === mainCamera) return SCENE
    return OTHER
  }

  const onResult = (slot: Slot): void => {
    if (slot.gen !== slot.submitGen) {
      slot.readBuffer.unmap()
      slot.state = FREE
      return
    }
    const range = slot.readBuffer.getMappedRange(0, slot.pairs * 16)
    // A fresh range per map in the browser (the API's allocation); reused when the same buffer comes back.
    if (slot.view === null || slot.view.buffer !== range) slot.view = new Uint32Array(range)
    const words = slot.view
    const base = ringHead * CLASS_COUNT
    for (let k = 0; k < CLASS_COUNT; k++) sums[base + k] = 0
    let allQuantised = slot.pairs >= 2
    let span = 0
    let spanFrames = 0
    let p = 0
    for (let f = 0; f < slot.frames; f++) {
      const frameEnd = slot.frameEnds[f]
      let first = Number.POSITIVE_INFINITY
      let last = Number.NEGATIVE_INFINITY
      for (; p < frameEnd; p++) {
        const i = p * 4
        const start = words[i + 1] * 4294967296 + words[i]
        const end = words[i + 3] * 4294967296 + words[i + 2]
        if (start % QUANTUM_NS !== 0 || end % QUANTUM_NS !== 0) allQuantised = false
        const ms = (end - start) / 1e6
        if (ms > 0 && ms < MAX_PASS_MS) {
          sums[base + slot.classes[p]] += ms
          if (start < first) first = start
          if (end > last) last = end
        }
      }
      const frameSpan = (last - first) / 1e6
      if (frameSpan > 0 && frameSpan < MAX_PASS_MS) {
        span += frameSpan
        spanFrames++
      }
    }
    slot.readBuffer.unmap()
    quantised = allQuantised
    sumFrames[ringHead] = slot.frames
    sumUntimed[ringHead] = slot.untimed
    sumSpan[ringHead] = span
    sumSpanFrames[ringHead] = spanFrames
    ringHead = (ringHead + 1) % RESULT_RING
    if (ringCount < RESULT_RING) ringCount++
    slot.state = FREE
  }

  for (let i = 0; device !== null && i < config.slots; i++) {
    const slot: Slot = {
      querySet: device.createQuerySet({ type: 'timestamp', count: queriesPerSlot, label: `perf.gpu.queries${i}` }),
      resolveBuffer: device.createBuffer({ size: bytesPerSlot, usage: QUERY_RESOLVE | COPY_SRC, label: `perf.gpu.resolve${i}` }),
      readBuffer: device.createBuffer({ size: bytesPerSlot, usage: COPY_DST | MAP_READ, label: `perf.gpu.read${i}` }),
      classes: new Uint8Array(queriesPerSlot / 2),
      pairs: 0,
      frames: 0,
      untimed: 0,
      frameEnds: new Uint16Array(config.framesPerSlot),
      state: FREE,
      gen: 0,
      submitGen: 0,
      view: null,
      onMapped: () => undefined,
      onFail: () => undefined,
    }
    slot.onMapped = (): void => onResult(slot)
    slot.onFail = (): void => {
      slot.state = FREE
    }
    slots.push(slot)
  }

  const submitSlot = (slot: Slot): void => {
    if (device === null) return
    const enc = device.createCommandEncoder()
    enc.resolveQuerySet(slot.querySet, 0, slot.pairs * 2, slot.resolveBuffer, 0)
    enc.copyBufferToBuffer(slot.resolveBuffer, 0, slot.readBuffer, 0, slot.pairs * 16)
    submitList[0] = enc.finish()
    device.queue.submit(submitList)
    submitList[0] = null
    slot.state = IN_FLIGHT
    slot.submitGen = slot.gen
    slot.readBuffer.mapAsync(MAP_MODE_READ, 0, slot.pairs * 16).then(slot.onMapped, slot.onFail)
  }

  const openSlot = (): void => {
    open = -1
    for (let i = 0; i < slots.length; i++) {
      if (slots[i].state === FREE) {
        const s = slots[i]
        s.state = RECORDING
        s.pairs = 0
        s.frames = 0
        s.untimed = 0
        open = i
        return
      }
    }
  }

  const stop = (): void => {
    active = false
    for (let i = 0; i < slots.length; i++) {
      const s = slots[i]
      s.gen++
      if (s.state === RECORDING) s.state = FREE
    }
    open = -1
    ringCount = 0
    ringHead = 0
    quantised = false
  }

  const timing: GpuTiming = {
    available: device !== null,
    config,
    get active() {
      return active
    },
    get queriesIssued() {
      return issued
    },
    frame(on: boolean): void {
      if (device === null) return
      if (!on) {
        if (active) stop()
        return
      }
      if (!active) {
        active = true
        sinceResolve = RESOLVE_EVERY
        framePairs = 0
        frameUntimed = 0
      }
      if (++sinceResolve >= RESOLVE_EVERY) {
        sinceResolve = 0
        resolveShadows()
      }
      // Close the frame that just ended.
      if (open >= 0) {
        const s = slots[open]
        if (framePairs > 0 || frameUntimed > 0) {
          s.frameEnds[s.frames] = s.pairs
          s.frames++
          s.untimed += frameUntimed
        }
        if (s.frames >= config.framesPerSlot) {
          if (s.pairs > 0) submitSlot(s)
          else s.state = FREE
          open = -1
        }
      }
      framePairs = 0
      frameUntimed = 0
      if (open < 0) openSlot()
    },
    pass(_type: string, descriptor: PassDescriptorLike): void {
      if (!active || open < 0 || framePairs >= config.maxPassesPerFrame) {
        // No open slot (all in flight): the frame goes untimed and does not count.
        if (active && open >= 0) frameUntimed++ // over maxPassesPerFrame
        if (descriptor.timestampWrites !== undefined) descriptor.timestampWrites = undefined
        return
      }
      const s = slots[open]
      const p = s.pairs
      s.classes[p] = _type === 'compute' ? OTHER : cls
      writes.querySet = s.querySet
      writes.beginningOfPassWriteIndex = p * 2
      writes.endOfPassWriteIndex = p * 2 + 1
      descriptor.timestampWrites = writes
      s.pairs = p + 1
      framePairs++
      issued += 2
    },
    read(out: GpuTimingReadout): GpuTimingReadout {
      out.available = device !== null
      out.quantised = quantised
      for (let k = 0; k < CLASS_COUNT; k++) out.classMs[k] = 0
      let frames = 0
      let untimed = 0
      let span = 0
      let spanFrames = 0
      let idx = ringHead
      for (let n = 0; n < ringCount && frames < config.windowFrames; n++) {
        idx = (idx - 1 + RESULT_RING) % RESULT_RING
        const base = idx * CLASS_COUNT
        for (let k = 0; k < CLASS_COUNT; k++) out.classMs[k] += sums[base + k]
        frames += sumFrames[idx]
        untimed += sumUntimed[idx]
        span += sumSpan[idx]
        spanFrames += sumSpanFrames[idx]
      }
      out.frames = frames
      out.totalMs = 0
      for (let k = 0; k < CLASS_COUNT; k++) {
        out.classMs[k] = frames > 0 ? out.classMs[k] / frames : 0
        out.totalMs += out.classMs[k]
      }
      out.untimed = frames > 0 ? untimed / frames : 0
      out.spanMs = spanFrames > 0 ? span / spanFrames : 0
      return out
    },
  }

  if (device === null) return timing

  // Patch three: own timestamp writes per pass, and the class of the render call running now.
  const backend = renderer.backend as { initTimestampQuery?: unknown }
  if (typeof backend.initTimestampQuery !== 'function') throw new Error('createGpuTiming: the backend has no initTimestampQuery()')
  backend.initTimestampQuery = (type: string, _uid: unknown, descriptor: PassDescriptorLike): void => timing.pass(type, descriptor)
  const target = renderer as unknown as Record<string, unknown>
  const inner = target.render as (this: unknown, scene: unknown, camera: unknown) => unknown
  if (typeof inner !== 'function') throw new Error('createGpuTiming: the renderer has no render()')
  target.render = function gpuTimedRender(this: unknown, s: RenderedScene, camera: unknown): unknown {
    if (!active) return inner.call(this, s, camera)
    const prev = cls
    cls = classify(s, camera)
    try {
      return inner.call(this, s, camera)
    } finally {
      cls = prev
    }
  }
  return timing
}
